import { useState, useEffect } from "react";
import { Mail, Phone, Loader2, Check, Clock, Undo2, StickyNote, Heart } from "lucide-react";
import { childrenLabel, fmtWhen, slugify } from "./adminUtils";

function EnquiryNote({ e, onSaveNote }) {
  const [draft, setDraft] = useState(e.note || "");
  const [saving, setSaving] = useState(false);
  const dirty = draft.trim() !== (e.note || "");
  const inputId = `note-${e.id}`;

  // Keep the textarea in step with server changes (e.g. after Refresh or a save).
  useEffect(() => {
    setDraft(e.note || "");
  }, [e.note]);

  const save = async () => {
    if (!dirty) return;
    setSaving(true);
    await onSaveNote(e, draft);
    setSaving(false);
  };

  return (
    <div className="mt-4" data-testid="enquiry-note">
      <label htmlFor={inputId} className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1.5">
        <StickyNote size={12} /> Private note
      </label>
      <div className="relative">
        <textarea
          id={inputId}
          value={draft}
          onChange={(ev) => setDraft(ev.target.value)}
          onBlur={save}
          rows={2}
          placeholder='e.g. "Offered Tue 16:30, waiting to hear back"'
          className="w-full resize-none rounded-xl border border-dashed border-[#FCD34D] bg-[#FFFBEB] px-3 py-2 text-sm text-[#334155] placeholder:text-[#B9A66F] focus:outline-none focus:border-[#F59E0B] focus:border-solid"
          data-testid={`note-input-${e.id}`}
        />
        {(dirty || saving) && (
          <span className="absolute right-2 bottom-2 inline-flex items-center gap-1 text-[11px] font-semibold text-[#B45309]" data-testid={`note-status-${e.id}`}>
            {saving ? <><Loader2 size={11} className="animate-spin" /> Saving</> : "Unsaved — click away to save"}
          </span>
        )}
      </div>
    </div>
  );
}

function ReplyToggleIcon({ busy, replied }) {
  if (busy) return <Loader2 size={15} className="animate-spin" />;
  if (replied) return <Undo2 size={15} />;
  return <Check size={15} />;
}

function StatusBadge({ replied }) {
  const cls = replied ? "bg-[#10B981] text-white" : "bg-[#F59E0B]/15 text-[#B45309]";
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap ${cls}`} data-testid="enquiry-status">
      {replied ? <><Check size={11} /> Replied</> : <><Clock size={11} /> Awaiting reply</>}
    </span>
  );
}

function ClassChips({ e, classFilter, onClassClick }) {
  const classes = (e.classes && e.classes.length ? e.classes : [e.program]).filter(Boolean);
  if (!classes.length) return null;
  return (
    <div className="mt-3 flex flex-wrap gap-1.5" data-testid="enquiry-classes">
      {classes.map((c) => (
        <button
          key={c}
          onClick={() => onClassClick(c)}
          title={classFilter === c ? "Clear class filter" : `Show everyone asking about ${c}`}
          className={`text-xs font-semibold px-3 py-1 rounded-full transition-colors ${classFilter === c ? "bg-[#0EA5E9] text-white" : "text-[#0EA5E9] bg-[#0EA5E9]/10 hover:bg-[#0EA5E9]/20"}`}
          data-testid={`class-chip-${slugify(c)}`}
        >
          {c}
        </button>
      ))}
    </div>
  );
}

export function EnquiryCard({ e, onToggleReplied, onSaveNote, busy, classFilter, onClassClick }) {
  const replyBtnCls = e.replied
    ? "bg-white text-[#64748B] border border-slate-200 hover:bg-slate-50"
    : "bg-[#10B981] text-white hover:-translate-y-0.5";
  return (
    <div
      className={`rounded-[1.5rem] p-6 border shadow-sm transition-colors ${e.replied ? "bg-[#F0FDF4] border-[#BBF7D0]" : "bg-white border-slate-100"}`}
      data-testid="enquiry-card"
      data-replied={e.replied ? "true" : "false"}
    >
      <div className="flex items-start justify-between gap-4">
        <h3 className="font-heading text-lg font-semibold text-[#0F172A]">{e.name}</h3>
        <div className="flex flex-wrap justify-end gap-1.5">
          <StatusBadge replied={e.replied} />
          {e.children && <span className="text-xs font-semibold text-[#10B981] bg-[#10B981]/10 px-3 py-1 rounded-full whitespace-nowrap">{childrenLabel(e.children)}</span>}
          {e.heard_from && <span className="text-xs font-semibold text-[#8B5CF6] bg-[#8B5CF6]/10 px-3 py-1 rounded-full whitespace-nowrap">via {e.heard_from}</span>}
        </div>
      </div>
      {e.referred_by && (
        <p className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0E9F6E] bg-[#10B981]/10 px-3 py-1.5 rounded-full" data-testid="enquiry-referred-by">
          <Heart size={13} /> Recommended by {e.referred_by}
        </p>
      )}
      <ClassChips e={e} classFilter={classFilter} onClassClick={onClassClick} />
      {e.days && e.days.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid="enquiry-days">
          {e.days.map((d) => (
            <span key={d} className="text-xs font-semibold text-[#F59E0B] bg-[#F59E0B]/10 px-2.5 py-1 rounded-full">{d}</span>
          ))}
        </div>
      )}
      <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-sm">
        <a href={`mailto:${e.email}`} className="inline-flex items-center gap-1.5 text-[#334155] hover:text-[#FF7A1A]"><Mail size={14} /> {e.email}</a>
        {e.phone && <a href={`tel:${e.phone}`} className="inline-flex items-center gap-1.5 text-[#334155] hover:text-[#FF7A1A]"><Phone size={14} /> {e.phone}</a>}
      </div>
      <p className="mt-4 text-[#334155] leading-relaxed whitespace-pre-line">{e.message}</p>
      <EnquiryNote e={e} onSaveNote={onSaveNote} />
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-slate-400">
          {fmtWhen(e.created_at)}
          {e.replied && e.replied_at && <span className="text-[#0E9F6E]"> · replied {fmtWhen(e.replied_at)}</span>}
        </p>
        <button
          onClick={() => onToggleReplied(e)}
          disabled={busy}
          className={`inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full transition-all disabled:opacity-50 ${replyBtnCls}`}
          data-testid={`toggle-replied-${e.id}`}
        >
          <ReplyToggleIcon busy={busy} replied={e.replied} />
          {e.replied ? "Mark as not replied" : "Mark as replied"}
        </button>
      </div>
    </div>
  );
}
